import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { flag } from '../config';
import OAuthSigningIn from './site/auth/OAuthSigningIn';
import { clearUrlSecret, readUrlSecret } from '../lib/urlSecrets';

/**
 * The Google (Emergent) sign-in return, mounted once by App.js: the `#session_id=` of the
 * return URL is exchanged through AuthContext.processOAuthSession; on success the fragment is
 * scrubbed (history.replaceState), on failure the visitor goes to "/". The index.html URL secret
 * guard has normally taken the id out of the address bar before any analytics tag loaded and
 * kept it for this path (lib/urlSecrets); readUrlSecret finds it there, or in the fragment when
 * it is still present. The kept copy is cleared when the exchange has finished (either way), so
 * it is never tried twice. While it works an overlay says so: with SLOT_MODEL on the brand
 * overlay (site/auth/OAuthSigningIn), off the legacy one below (unchanged).
 */

const OAuthCallback = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const navigate = useNavigate();
  const { processOAuthSession } = useAuth();

  useEffect(() => {
    const handleOAuthCallback = async () => {
      // The session_id of an OAuth return: in the URL fragment, or kept by the index.html guard.
      const sessionId = readUrlSecret('session_id', { part: 'hash' });
      if (!sessionId) {
        // No OAuth callback in progress - silently return
        return;
      }

      if (sessionId && !isProcessing) {
        setIsProcessing(true);
        
        try {
          // Use the processOAuthSession from AuthContext
          const result = await processOAuthSession(sessionId);

          if (result.success) {
            // Clean URL fragment
            window.history.replaceState(null, '', window.location.pathname);
          } else {
            console.error('OAuth session processing failed:', result.error);
            navigate('/');
          }
        } catch (error) {
          console.error('OAuth callback error:', error);
          navigate('/');
        } finally {
          clearUrlSecret('session_id', { part: 'hash' });
          setIsProcessing(false);
        }
      }
    };

    handleOAuthCallback();
  }, [navigate, processOAuthSession, isProcessing]);

  if (isProcessing) {
    return flag('SLOT_MODEL') ? <OAuthSigningIn /> : <LegacyOverlay />;
  }

  return null;
};

/** The pre-redesign overlay (SLOT_MODEL off), markup unchanged. */
function LegacyOverlay() {
  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
    >
      <div 
        className="bg-white rounded-2xl p-8 text-center"
        style={{ maxWidth: '400px' }}
      >
        <div className="mb-4">
          <div 
            className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"
            style={{ borderColor: '#FF7A1A', borderRightColor: 'transparent' }}
          />
        </div>
        <h3 
          className="text-xl font-bold mb-2"
          style={{ fontFamily: 'Fraunces, serif', color: '#111827' }}
        >
          Signing you in...
        </h3>
        <p style={{ fontFamily: 'Inter, sans-serif', color: '#6B7280' }}>
          Please wait a moment
        </p>
      </div>
    </div>
  );
}

export default OAuthCallback;
